// Bearings as the measure tool prints and reads them, ported from the Android app's
// MeasureBearings.kt (and the north/unit settings in MeasureToolPrefs.kt): "047°M" in degrees,
// "0836 M" in mils (6400 to the circle). Every bearing is computed true and converted here —
// magnetic = true − declination, grid = true − convergence.

import { GeoPoint, normalizeBearing, toDegrees, toRadians } from './geodesy';
import { MeasureGridFormat, formatGridReference } from './gridReference';
import { utmZone } from './transverseMercator';
import { declinationAt } from './wmm2025';
import { doubleMod, floorMod, parseDecimal, roundHalfUp } from './numbers';

export { parseDecimal, roundHalfUp } from './numbers';

/** Which north the bearings are taken from. Android's enum names, so stored values match. */
export type MeasureNorthReference = 'True' | 'Magnetic' | 'Grid';
export const NORTH_REFERENCES: readonly MeasureNorthReference[] = ['True', 'Magnetic', 'Grid'];
export const DEFAULT_NORTH_REFERENCE: MeasureNorthReference = 'Magnetic';

/** The letter printed after every bearing. Never localised. */
export const NORTH_LETTERS: Record<MeasureNorthReference, string> = { True: 'T', Magnetic: 'M', Grid: 'G' };

/** Degrees (360 to the circle) or NATO mils (6400 to the circle). */
export type MeasureBearingUnit = 'Degrees' | 'Mils';
export const BEARING_UNITS: readonly MeasureBearingUnit[] = ['Degrees', 'Mils'];
export const DEFAULT_BEARING_UNIT: MeasureBearingUnit = 'Degrees';

export function northReferenceFromStored(raw: string | null | undefined): MeasureNorthReference | null {
  return NORTH_REFERENCES.find((reference) => reference === raw) ?? null;
}

export function bearingUnitFromStored(raw: string | null | undefined): MeasureBearingUnit | null {
  return BEARING_UNITS.find((unit) => unit === raw) ?? null;
}

export const DEGREES_PER_CIRCLE = 360.0;
export const MILS_PER_CIRCLE = 6400.0;

/**
 * The corrections between true north and the walker's chosen north, both taken at the start
 * point: magnetic declination (east positive, from WMM2025) and grid convergence for the grid.
 */
export interface NorthCorrections {
  declination: number;
  convergence: number;
  /**
   * Whether the chosen grid exists at the start (its reference would not read "- -"). Outside it
   * there is no grid north to speak of — Irish Grid convergence in the Alps comes out at 13° — so
   * grid bearings fall back to true north and say "T" ([effectiveReference]).
   */
  gridAvailable: boolean;
}

export const NO_CORRECTIONS: NorthCorrections = { declination: 0, convergence: 0, gridAvailable: true };

/** Android's `NorthCorrections(declination, convergence, gridAvailable = true)`. */
export function northCorrections(declination: number, convergence: number, gridAvailable = true): NorthCorrections {
  return { declination, convergence, gridAvailable };
}

/** The north bearings are actually given in: grid north only where the grid applies. */
export function effectiveReference(corrections: NorthCorrections, chosen: MeasureNorthReference): MeasureNorthReference {
  return chosen === 'Grid' && !corrections.gridAvailable ? 'True' : chosen;
}

function wrapLongitude(longitude: number): number {
  let lon = (longitude + 180.0) % 360.0;
  if (lon < 0) lon += 360.0;
  return lon - 180.0;
}

/**
 * The grid's own central meridian: Irish Grid and ITM −8°, OSGB −2°, UTM/MGRS the zone's (with
 * the Norway and Svalbard exceptions). Lat/Long has none.
 */
export function gridCentralMeridian(format: MeasureGridFormat, latitude: number, longitude: number): number | null {
  switch (format) {
    case 'IrishGrid':
    case 'Itm':
      return -8.0;
    case 'Osgb':
      return -2.0;
    case 'Utm':
    case 'Mgrs':
      if (latitude < -80.0 || latitude > 84.0) return null;
      return 6.0 * utmZone(latitude, wrapLongitude(longitude)) - 183.0;
    default:
      return null;
  }
}

/** Grid convergence γ = atan(tan(λ − λ0) · sin φ): how far grid north sits east of true north. */
export function gridConvergence(format: MeasureGridFormat, latitude: number, longitude: number): number {
  const lambda0 = gridCentralMeridian(format, latitude, longitude);
  if (lambda0 === null) return 0.0;
  const dLambda = toRadians(wrapLongitude(longitude - lambda0));
  return toDegrees(Math.atan(Math.tan(dLambda) * Math.sin(toRadians(latitude))));
}

/** The corrections at the start point for [gridFormat] on [date] (Android `NorthCorrections.at`). */
export function northCorrectionsAt(point: GeoPoint, gridFormat: MeasureGridFormat, date: Date): NorthCorrections {
  const gridAvailable = formatGridReference(gridFormat, point.lat, point.lng) !== null;
  return {
    declination: declinationAt(point.lat, point.lng, date),
    convergence: gridAvailable ? gridConvergence(gridFormat, point.lat, point.lng) : 0.0,
    gridAvailable,
  };
}

export function toReference(trueBearing: number, reference: MeasureNorthReference, corrections: NorthCorrections): number {
  switch (reference) {
    case 'Magnetic': return normalizeBearing(trueBearing - corrections.declination);
    case 'Grid': return normalizeBearing(trueBearing - corrections.convergence);
    default: return normalizeBearing(trueBearing);
  }
}

export function toTrue(bearing: number, reference: MeasureNorthReference, corrections: NorthCorrections): number {
  switch (reference) {
    case 'Magnetic': return normalizeBearing(bearing + corrections.declination);
    case 'Grid': return normalizeBearing(bearing + corrections.convergence);
    default: return normalizeBearing(bearing);
  }
}

/** A bearing already in the walker's reference, printed with its letter (Android `format`). */
export function formatBearing(bearing: number, unit: MeasureBearingUnit, reference: MeasureNorthReference): string {
  const b = normalizeBearing(bearing);
  if (unit === 'Mils') {
    return `${String(floorMod(roundHalfUp(b * MILS_PER_CIRCLE / DEGREES_PER_CIRCLE), 6400)).padStart(4, '0')} ${NORTH_LETTERS[reference]}`;
  }
  return `${String(floorMod(roundHalfUp(b), 360)).padStart(3, '0')}°${NORTH_LETTERS[reference]}`;
}

/** A true bearing converted to the walker's north and printed: the yellow figure in the sheet. */
export function formatTrue(
  trueBearing: number,
  unit: MeasureBearingUnit,
  reference: MeasureNorthReference,
  corrections: NorthCorrections,
): string {
  return formatBearing(toReference(trueBearing, reference, corrections), unit, reference);
}

/** The back bearing: the same line read from the far end. */
export function formatBack(
  trueBearing: number,
  unit: MeasureBearingUnit,
  reference: MeasureNorthReference,
  corrections: NorthCorrections,
): string {
  return formatTrue(trueBearing + 180.0, unit, reference, corrections);
}

/**
 * What the range sheet's bearing field holds for a heading: the bearing in the walker's north and
 * unit, as a whole number with no letter ("47", "836").
 */
export function fieldValue(
  trueHeading: number,
  unit: MeasureBearingUnit,
  reference: MeasureNorthReference,
  corrections: NorthCorrections,
): string {
  const b = toReference(trueHeading, reference, corrections);
  if (unit === 'Mils') return String(floorMod(roundHalfUp(b * MILS_PER_CIRCLE / DEGREES_PER_CIRCLE), 6400));
  return String(floorMod(roundHalfUp(b), 360));
}

/**
 * A typed bearing in [unit] — "47", "047.5", "47,5", "-90", "+90", "450" — wrapped onto the circle:
 * degrees modulo 360, mils modulo 6400, so the result is in [0, 360) / [0, 6400). Any finite
 * number is a bearing, as in the mockup: "450" is 90°, "-90" is 270°, "7200" mils is 800. Null only
 * when the text is not a plain decimal number — empty, a lone sign, two separators, an exponent.
 * (Android `parseInput`.)
 */
export function parseBearingInput(text: string, unit: MeasureBearingUnit): number | null {
  const value = parseDecimal(text, true);
  if (value === null) return null;
  const circle = unit === 'Mils' ? MILS_PER_CIRCLE : DEGREES_PER_CIRCLE;
  const wrapped = doubleMod(value, circle);
  // -1e-15 wraps to 360 − 1e-15, which rounds to 360.0 — not on the circle. "+ 0" turns -0 into 0.
  return wrapped >= circle ? 0.0 : wrapped + 0.0;
}

/** A parsed field value ([parseBearingInput]) as a true bearing in degrees. */
export function inputToTrue(
  value: number,
  unit: MeasureBearingUnit,
  reference: MeasureNorthReference,
  corrections: NorthCorrections,
): number {
  const degrees = unit === 'Mils' ? value * DEGREES_PER_CIRCLE / MILS_PER_CIRCLE : value;
  return toTrue(degrees, reference, corrections);
}

/** The range field's ceiling: 100 km. */
export const MAX_RANGE_METERS = 100_000.0;

/** The range field: metres, more than 0 and at most 100 km (Android `MeasureRangeInput.parseDistance`). */
export function parseDistance(text: string): number | null {
  const value = parseDecimal(text);
  return value !== null && value > 0.0 && value <= MAX_RANGE_METERS ? value : null;
}
